import type { PropsWithChildren } from "react";
import { FlipHorizontal2, FlipVertical2, RotateCcw, RotateCw } from "lucide-react";

import type { AspectPreset, EditorState } from "../../types/image";
import { Button } from "../ui/Button";
import { Slider } from "../ui/Slider";

interface EditorToolbarProps {
  editorState: EditorState;
  renderStatus: "idle" | "loading" | "success" | "error";
  onAspectChange: (preset: AspectPreset) => void;
  onRotateLeft: () => void;
  onRotateRight: () => void;
  onFlipHorizontal: () => void;
  onFlipVertical: () => void;
  onSetBrightness: (value: number) => void;
  onSetContrast: (value: number) => void;
  onSetSaturation: (value: number) => void;
  onSetBlur: (value: number) => void;
  onSetSharpen: (value: number) => void;
  onSetZoom: (value: number) => void;
  onToggleCompare: () => void;
  onResetEdits: () => void;
  onExport: () => void;
}

const aspectPresets: AspectPreset[] = ["free", "1:1", "4:3", "16:9"];

export function EditorToolbar({
  editorState,
  renderStatus,
  onAspectChange,
  onRotateLeft,
  onRotateRight,
  onFlipHorizontal,
  onFlipVertical,
  onSetBrightness,
  onSetContrast,
  onSetSaturation,
  onSetBlur,
  onSetSharpen,
  onSetZoom,
  onToggleCompare,
  onResetEdits,
  onExport
}: EditorToolbarProps) {
  return (
    <div className="space-y-5">
      <ToolbarGroup title="Crop">
        <div className="grid grid-cols-2 gap-2">
          {aspectPresets.map((preset) => (
            <Button
              key={preset}
              className="justify-start"
              variant={editorState.aspectPreset === preset ? "primary" : "secondary"}
              onClick={() => onAspectChange(preset)}
            >
              {preset}
            </Button>
          ))}
        </div>
        <Slider
          label="Zoom"
          max={3}
          min={1}
          step={0.05}
          value={editorState.zoom}
          valueLabel={`${editorState.zoom.toFixed(2)}x`}
          onChange={(event) => onSetZoom(Number(event.target.value))}
        />
      </ToolbarGroup>

      <ToolbarGroup title="Transform">
        <div className="grid grid-cols-2 gap-2">
          <Button variant="secondary" onClick={onRotateLeft}>
            <RotateCcw className="h-4 w-4" />
            Rotate left
          </Button>
          <Button variant="secondary" onClick={onRotateRight}>
            <RotateCw className="h-4 w-4" />
            Rotate right
          </Button>
          <Button variant="secondary" onClick={onFlipHorizontal}>
            <FlipHorizontal2 className="h-4 w-4" />
            Flip H
          </Button>
          <Button variant="secondary" onClick={onFlipVertical}>
            <FlipVertical2 className="h-4 w-4" />
            Flip V
          </Button>
        </div>
      </ToolbarGroup>

      <ToolbarGroup title="Adjust">
        <div className="space-y-4">
          <Slider
            label="Brightness"
            max={150}
            min={50}
            step={1}
            value={editorState.brightness}
            valueLabel={`${editorState.brightness}%`}
            onChange={(event) => onSetBrightness(Number(event.target.value))}
          />
          <Slider
            label="Contrast"
            max={150}
            min={50}
            step={1}
            value={editorState.contrast}
            valueLabel={`${editorState.contrast}%`}
            onChange={(event) => onSetContrast(Number(event.target.value))}
          />
          <Slider
            label="Saturation"
            max={180}
            min={0}
            step={1}
            value={editorState.saturation}
            valueLabel={`${editorState.saturation}%`}
            onChange={(event) => onSetSaturation(Number(event.target.value))}
          />
          <Slider
            label="Blur"
            max={6}
            min={0}
            step={0.25}
            value={editorState.blur}
            valueLabel={`${editorState.blur.toFixed(2)} px`}
            onChange={(event) => onSetBlur(Number(event.target.value))}
          />
          <Slider
            label="Sharpen"
            max={100}
            min={0}
            step={1}
            value={editorState.sharpen}
            valueLabel={`${editorState.sharpen}%`}
            onChange={(event) => onSetSharpen(Number(event.target.value))}
          />
        </div>
      </ToolbarGroup>

      <ToolbarGroup title="Compare & export">
        <div className="grid gap-2 sm:grid-cols-2">
          <Button variant="secondary" onClick={onToggleCompare}>
            Show {editorState.compareMode === "before" ? "after" : "before"}
          </Button>
          <Button disabled={renderStatus === "loading"} variant="primary" onClick={onExport}>
            Export image
          </Button>
        </div>
        <Button fullWidth variant="ghost" onClick={onResetEdits}>
          Reset all edits
        </Button>
      </ToolbarGroup>
    </div>
  );
}

function ToolbarGroup({ title, children }: PropsWithChildren<{ title: string }>) {
  return (
    <section className="space-y-4 rounded-[24px] border border-white/8 bg-white/[0.03] p-4">
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-[0.18em] text-ink-muted">{title}</p>
      </div>
      {children}
    </section>
  );
}
